import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { Message } from "@langchain/langgraph-sdk";
import { deriveTurns } from "./useTurns";

/**
 * Walk up the DOM from `el` to find the nearest scrollable ancestor.
 * Returns `el` itself if it scrolls, or the first parent with
 * overflow-y auto/scroll, or `document.documentElement` as fallback.
 */
function findScrollParent(el: HTMLElement): HTMLElement {
  // Check overflow-y style first — an element with overflow-y: auto/scroll
  // IS the scroll container regardless of whether content currently overflows.
  // Checking scrollHeight > clientHeight first caused the List to be skipped
  // when content fit, leading to scroll operations targeting a wrong parent.
  const style = getComputedStyle(el);
  if (style.overflowY === "auto" || style.overflowY === "scroll") {
    return el;
  }

  let parent = el.parentElement;
  while (parent) {
    const parentStyle = getComputedStyle(parent);
    if (parentStyle.overflowY === "auto" || parentStyle.overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.documentElement;
}

export interface UseScrollAnchorOptions {
  threshold?: number;
  behavior?: ScrollBehavior;
  /** Minimum ms between auto-scrolls. First fires immediately, then throttles. */
  throttleMs?: number;
  /** When true, the current turn is still streaming — don't advance the acked baseline. */
  isStreaming?: boolean;
}

export interface UseScrollAnchorReturn {
  shouldAutoScroll: boolean;
  newTurnCount: number;
  scrollToBottom: () => void;
  /** Previous messages array — exposed for debugging scroll glitches. */
  prevMessages: Message[];
}

export function useScrollAnchor(
  containerRef: RefObject<HTMLElement | null>,
  anchorRef: RefObject<HTMLElement | null>,
  messages: Message[],
  options: UseScrollAnchorOptions = {}
): UseScrollAnchorReturn {
  const { threshold = 50, behavior = "smooth", throttleMs = 1000, isStreaming = false } = options;

  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [newTurnCount, setNewTurnCount] = useState(0);

  const isProgrammaticScroll = useRef(false);
  const prevMessagesRef = useRef<Message[]>(messages);
  const lastScrollTime = useRef(0);
  const pendingScroll = useRef<ReturnType<typeof setTimeout> | null>(null);

  // "Acknowledged" baseline — updated only when the user is at the bottom
  // and NOT streaming. A streaming message can never be fully acked.
  const ackedTurnCount = useRef(deriveTurns(messages).length);
  const ackedMessageCount = useRef(messages.length);

  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;

  const shouldAutoScrollRef = useRef(shouldAutoScroll);
  shouldAutoScrollRef.current = shouldAutoScroll;

  /**
   * Advance the acked baseline to the current message state.
   * No-op while streaming — the latest turn is still in flux.
   */
  const ackCurrentState = useCallback(() => {
    if (isStreamingRef.current) return;
    ackedTurnCount.current = deriveTurns(prevMessagesRef.current).length;
    ackedMessageCount.current = prevMessagesRef.current.length;
  }, []);

  /**
   * Is the anchor element visible within the container's scroll area?
   * The container (Chat.Messages.List) is always the scroll container,
   * so its bounding rect represents the actual visible message area.
   */
  const isAnchorVisible = useCallback(() => {
    // const container = containerRef.current;
    const scrollTarget = containerRef.current ? findScrollParent(containerRef.current) : null;
    const anchor = anchorRef.current;
    if (!scrollTarget || !anchor) return false;

    const containerRect = scrollTarget.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();

    const isVisible = anchorRect.top >= containerRect.top && anchorRect.top <= containerRect.bottom + threshold;
    return isVisible;
  }, [containerRef, anchorRef, threshold]);

  const doScroll = useCallback(() => {
    if (!anchorRef.current) return;
    if (isAnchorVisible()) return; // already in view, nothing to do
    isProgrammaticScroll.current = true;
    lastScrollTime.current = Date.now();

    const container = containerRef.current;
    const scrollTarget = container ? findScrollParent(container) : null;
    // Scroll only the container — scrollIntoView would also scroll every
    // ancestor scroll container (e.g. the page's main), which yanks the
    // user away from sibling content like the AdPreview.
    if (scrollTarget) {
      scrollTarget.scrollTo({ top: scrollTarget.scrollHeight, behavior });
    } else {
      anchorRef.current.scrollIntoView({ behavior });
    }

    const clearGuard = () => {
      isProgrammaticScroll.current = false;
    };

    if (scrollTarget) {
      const onScrollEnd = () => {
        clearGuard();
        scrollTarget.removeEventListener("scrollend", onScrollEnd);
      };
      scrollTarget.addEventListener("scrollend", onScrollEnd, { once: true });
    }
    setTimeout(clearGuard, 500);
  }, [anchorRef, containerRef, behavior, isAnchorVisible]);

  /** Throttled scroll: fires immediately, then at most once per throttleMs. */
  const throttledScroll = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastScrollTime.current;

    if (elapsed >= throttleMs) {
      // Enough time passed — scroll now
      if (pendingScroll.current) {
        clearTimeout(pendingScroll.current);
        pendingScroll.current = null;
      }
      doScroll();
    } else if (!pendingScroll.current) {
      // Schedule a trailing scroll for the remaining time
      pendingScroll.current = setTimeout(() => {
        pendingScroll.current = null;
        doScroll();
      }, throttleMs - elapsed);
    }
  }, [doScroll, throttleMs]);

  // Cleanup pending timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingScroll.current) {
        clearTimeout(pendingScroll.current);
      }
    };
  }, []);

  // Watch for content growth (handles thinking indicator, intent buttons, todos, etc.)
  // Uses MutationObserver because the container has overflow-y:auto with flex constraints,
  // so its border box doesn't change when content grows — ResizeObserver won't fire.
  // Calls scrollIntoView directly (bypassing isAnchorVisible threshold) because small
  // additions like the ThinkingIndicator (~50px) can fall within the 50px threshold
  // and get skipped by doScroll().
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const scrollTarget = findScrollParent(container);

    let prevScrollHeight = scrollTarget.scrollHeight;

    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        const newScrollHeight = scrollTarget.scrollHeight;
        if (newScrollHeight > prevScrollHeight && shouldAutoScrollRef.current) {
          // Scroll only the container, not every ancestor — see doScroll.
          //
          // Always instant here, never `behavior` (smooth). This observer
          // fires on every DOM mutation during token streaming (~10/s), and
          // each smooth scrollTo cancels the in-flight smooth animation and
          // restarts it — the bottom of the list (thinking indicator,
          // todos) visibly bobs and stutters for the whole stream. Instant
          // keeps the list pinned with no animation to interrupt. Discrete
          // jumps (new turns) still scroll smoothly via throttledScroll.
          isProgrammaticScroll.current = true;
          scrollTarget.scrollTo({ top: scrollTarget.scrollHeight, behavior: "instant" });
          isProgrammaticScroll.current = false;
        }
        prevScrollHeight = newScrollHeight;
      });
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [containerRef, anchorRef, behavior]);

  const scrollToBottom = useCallback(() => {
    if (pendingScroll.current) {
      clearTimeout(pendingScroll.current);
      pendingScroll.current = null;
    }
    doScroll();
    setShouldAutoScroll(true);
    setNewTurnCount(0);
    ackCurrentState();
  }, [doScroll, ackCurrentState]);

  const lastScrollTop = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Listen on the actual scroll parent, not just the container ref.
    // In some layouts (e.g. Brainstorm) a parent element scrolls instead of the List.
    const scrollParent = findScrollParent(container);
    lastScrollTop.current = scrollParent.scrollTop;

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      const currentScrollTop = scrollParent.scrollTop;
      const scrolledUp = currentScrollTop < lastScrollTop.current;
      lastScrollTop.current = currentScrollTop;

      // Check anchor visibility first. If the user is at/near the bottom,
      // always stay in auto-scroll mode — even if momentum/rubber-band
      // scrolling causes tiny upward movements (1-2px oscillations on macOS).
      if (isAnchorVisible()) {
        setShouldAutoScroll(true);
        setNewTurnCount(0);
        ackCurrentState();
        return;
      }

      // Away from the bottom — scrolling up disables auto-scroll
      if (scrolledUp) {
        setShouldAutoScroll(false);
      }
    };

    scrollParent.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollParent.removeEventListener("scroll", handleScroll);
  }, [containerRef, isAnchorVisible]);

  useEffect(() => {
    prevMessagesRef.current = messages;

    if (shouldAutoScroll) {
      ackCurrentState();
      throttledScroll();
    } else {
      // Compute unseen turns from the acknowledged baseline
      const currentTurns = deriveTurns(messages).length;
      const unseenTurns = currentTurns - ackedTurnCount.current;

      // If no new turn started but messages grew (mid-turn streaming), count as 1
      const hasUnseenContent = messages.length > ackedMessageCount.current;

      const newCount = unseenTurns > 0 ? unseenTurns : hasUnseenContent ? 1 : 0;
      setNewTurnCount(newCount);
    }
  }, [messages, shouldAutoScroll, isStreaming, throttledScroll]);

  return { shouldAutoScroll, newTurnCount, scrollToBottom, prevMessages: prevMessagesRef.current };
}
